import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import { utils } from 'utils';
import {
  Button,
  Heading,
  Stack,
  Form,
  createSchema,
  string,
} from '@guestyci/arc';
import { useBreakpoint } from '../../../utils/useBreakpoint';
import PasswordInput from './PasswordInput';
import ValidationRules from './ValidationRules';
import { FormLayout } from '../../layout';

const CustomResetPassword = ({
  onCreatePassword,
  error: externalError,
  isSubmitting,
}) => {
  const isDesktop = useBreakpoint('lg');
  const logEvent = utils.eventsLogger?.unifiedLoginDio?.track;
  const isOnboardingFlow = sessionStorage.getItem('isOnboardingFlow');

  const createPasswordFormSchema = createSchema({
    password: string
      .min(8, { message: t('Minimum 8 characters') })
      .regex(/\d/, { message: t('One number') })
      .regex(/[a-z]/, { message: t('One lowercase letter') })
      .regex(/[A-Z]/, { message: t('One uppercase letter') }),
  });

  const form = useForm({
    resolver: zodResolver(createPasswordFormSchema),
    defaultValues: {
      password: '',
    },
    mode: 'onBlur',
  });

  const logCreatePasswordEvent = (submitted) => {
    if (isOnboardingFlow && logEvent) {  
      if (submitted === false) {
        logEvent('registration_Choose_password', 'submit', {
          properties: 'create_password/error',
        });
      } else {
        logEvent('registration_Choose_password', 'submit', {
          properties: 'create_password_signup',
        });
      }
    }
  }

  const handleSubmit = (data) => {
    const submitted = onCreatePassword(data.password);
    logCreatePasswordEvent(submitted);
  };

  return (
    <FormLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="gst-flex gst-flex-col gst-flex-1">
          <Stack spacing={6} justify="between" className="gst-flex-1 gst-pt-6 lg:gst-pt-0">
            <Stack spacing={6}>
              <Heading variant={isDesktop ? 'h1' : 'h2'}>
                {t('Create your password')}
              </Heading>
              <PasswordInput
                form={form}
                isSubmitting={isSubmitting}
                externalError={externalError}
              />
              <ValidationRules />
            </Stack>

            <Button
              type="submit"
              className="gst-w-full lg:gst-w-auto"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('Create password')}
            </Button>
          </Stack>
        </form>
      </Form>
    </FormLayout>
  );
};

export default CustomResetPassword;
